/**
 * Login page with OAuth provider selection (Google, Okta, Microsoft).
 * Includes dev login bypass with email input for development environments.
 */
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * Login page
 *
 * Supports multiple authentication providers:
 * - Google OAuth 2.0
 * - Okta OIDC
 * - Microsoft Azure AD
 * - Dev bypass (development only) - uses browser redirect to simulate OAuth flow
 */
export function Login() {
	const [devEmail, setDevEmail] = useState("");

	/**
	 * Initiates OAuth login by redirecting to the backend OAuth endpoint
	 * @param provider - the OAuth provider to use
	 */
	const handleOAuthLogin = (provider: "google" | "okta" | "microsoft") => {
		// Redirect to backend OAuth endpoint (API is on a different domain in production)
		const apiUrl = (import.meta.env.VITE_API_URL as string | undefined) ?? "";
		window.location.href = `${apiUrl}/auth/${provider}`;
	};

	/**
	 * Initiates dev login by redirecting to the dev auth endpoint
	 * @param email - optional email for dev login, defaults to super admin
	 */
	const handleDevLogin = (email?: string) => {
		// Use browser redirect (not tRPC) to simulate OAuth flow
		// This ensures sessionStorage persists across the redirect, matching real OAuth behavior

		// If no email specified (Super Admin login), clear any pending invite token
		// Super Admin login should bypass invite processing entirely
		if (!email) {
			sessionStorage.removeItem("pendingInviteToken");
		}

		const apiUrl = (import.meta.env.VITE_API_URL as string | undefined) ?? "";
		const emailParam = email ? `?email=${encodeURIComponent(email)}` : "";
		window.location.href = `${apiUrl}/auth/dev${emailParam}`;
	};

	const isDev = import.meta.env.DEV;

	return (
		<div className="flex min-h-screen items-center justify-center bg-white">
			<div className="w-full max-w-md space-y-8 rounded-lg border border-sidebar-border bg-sidebar p-8 shadow-2xl drop-shadow-2xl">
				{/* Logo */}
				<div className="text-center">
					<img
						src="/unios_small.jpg"
						alt="Unios"
						className="mx-auto"
						style={{ height: 64, width: "auto" }}
					/>
					<h1 className="mt-4 text-2xl font-bold text-sidebar-foreground">Welcome to Unios</h1>
					<p className="mt-2 text-sm text-sidebar-muted">
						Sign in to access your workflow dashboard
					</p>
				</div>

				{/* OAuth Providers */}
				<div className="space-y-3">
					<Button
						variant="outline"
						className="w-full"
						onClick={() => { handleOAuthLogin("google"); }}
					>
						<svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
							<path
								fill="currentColor"
								d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
							/>
							<path
								fill="currentColor"
								d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
							/>
							<path
								fill="currentColor"
								d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
							/>
							<path
								fill="currentColor"
								d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
							/>
						</svg>
						Sign in with Google
					</Button>

					<Button
						variant="outline"
						className="w-full"
						onClick={() => { handleOAuthLogin("okta"); }}
					>
						<svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
							<path
								fill="currentColor"
								d="M12 0C5.389 0 0 5.389 0 12s5.389 12 12 12 12-5.389 12-12S18.611 0 12 0zm0 18c-3.314 0-6-2.686-6-6s2.686-6 6-6 6 2.686 6 6-2.686 6-6 6z"
							/>
						</svg>
						Sign in with Okta
					</Button>

					<Button
						variant="outline"
						className="w-full"
						onClick={() => { handleOAuthLogin("microsoft"); }}
					>
						<svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
							<path fill="#f25022" d="M1 1h10v10H1z" />
							<path fill="#00a4ef" d="M1 13h10v10H1z" />
							<path fill="#7fba00" d="M13 1h10v10H13z" />
							<path fill="#ffb900" d="M13 13h10v10H13z" />
						</svg>
						Sign in with Microsoft
					</Button>
				</div>

				{/* Dev Bypass (development only) */}
				{isDev && (
					<>
						<div className="relative">
							<div className="absolute inset-0 flex items-center">
								<div className="w-full border-t" />
							</div>
							<div className="relative flex justify-center text-xs uppercase">
								<span className="bg-sidebar px-2 text-sidebar-muted">
									Development Only
								</span>
							</div>
						</div>

						<Button
							variant="secondary"
							className="w-full"
							onClick={() => { handleDevLogin(); }}
						>
							Dev Login (Super Admin)
						</Button>

						{/* Email input for testing invites */}
						<div className="flex gap-2">
							<Input
								type="email"
								placeholder="test@example.com"
								value={devEmail}
								onChange={(e) => { setDevEmail(e.target.value); }}
								onKeyDown={(e) => {
									if (e.key === "Enter" && devEmail) {
										e.preventDefault();
										handleDevLogin(devEmail);
									}
								}}
								className="flex-1 bg-background text-foreground"
							/>
							<Button
								variant="outline"
								onClick={() => { handleDevLogin(devEmail || undefined); }}
								disabled={!devEmail}
							>
								Login as Email
							</Button>
						</div>
					</>
				)}
			</div>
		</div>
	);
}
